/**
 * AdjacencyMapSyncStatusChip (WQ-210)
 *
 * Single persistent badge in the adjacency-map header showing the sync
 * state across all 7 sub-tabs. Click opens the detail popover.
 *
 * Health → badge class:
 *   healthy  → badge-success (green)
 *   stale    → badge-warning (amber)
 *   errored  → badge-error   (red)
 *   disabled → badge-secondary (gray)
 *   unknown  → badge-secondary (gray)
 */
import React, { useState } from 'react';
import {
  useAdjacencyMapSyncStatus,
  SyncHealth,
} from '../../hooks/useAdjacencyMapSyncStatus';
import AdjacencyMapSyncStatusPopover from './AdjacencyMapSyncStatusPopover';

interface Props {
  mapId: string;
  tenantId: string | null;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const HEALTH_LABEL: Record<SyncHealth, string> = {
  healthy: 'Sync healthy',
  stale: 'Sync stale',
  errored: 'Sync errored',
  disabled: 'Sync disabled',
  unknown: 'Sync unknown',
};

const HEALTH_BADGE_CLASS: Record<SyncHealth, string> = {
  healthy: 'badge badge-success',
  stale: 'badge badge-warning',
  errored: 'badge badge-error',
  disabled: 'badge badge-secondary',
  unknown: 'badge badge-secondary',
};

const HEALTH_DOT_COLOR: Record<SyncHealth, string> = {
  healthy: 'var(--success, #10b981)',
  stale: 'var(--warning, #f59e0b)',
  errored: 'var(--error, #ef4444)',
  disabled: 'var(--text-muted, #6b7280)',
  unknown: 'var(--text-muted, #6b7280)',
};

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return iso;
  const diff = Date.now() - d;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const AdjacencyMapSyncStatusChip: React.FC<Props> = ({
  mapId,
  tenantId,
  authFetch,
}) => {
  const { status, isLoading } = useAdjacencyMapSyncStatus(
    mapId,
    tenantId,
    authFetch,
  );
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (isLoading) {
    return (
      <span
        className="badge badge-secondary"
        data-testid="sync-chip-loading"
        style={{ padding: '2px 8px', fontSize: 11 }}
      >
        Sync…
      </span>
    );
  }

  const label =
    status.health === 'healthy' && status.lastScheduledSync
      ? `Synced ${formatRelative(status.lastScheduledSync)}`
      : status.health === 'stale' && status.lastScheduledSync
        ? `Sync stale · ${formatRelative(status.lastScheduledSync)}`
        : HEALTH_LABEL[status.health];

  const tooltip = status.lastScheduledSync
    ? `Last: ${new Date(status.lastScheduledSync).toLocaleString()} — ${status.summary}`
    : status.summary;

  return (
    <>
      <button
        type="button"
        className={HEALTH_BADGE_CLASS[status.health]}
        onClick={() => setPopoverOpen(true)}
        title={tooltip}
        data-testid="sync-status-chip"
        data-sync-health={status.health}
        style={{
          cursor: 'pointer',
          padding: '2px 10px',
          fontSize: 11,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: 'none',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: HEALTH_DOT_COLOR[status.health],
          }}
        />
        {label}
      </button>
      {popoverOpen && (
        <AdjacencyMapSyncStatusPopover
          status={status}
          onClose={() => setPopoverOpen(false)}
        />
      )}
    </>
  );
};

export default AdjacencyMapSyncStatusChip;
